<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRevendedorStore } from '@/stores/revendedor';
import { useNotificationStore } from '@/stores/notification';
import DesativarRevendedorModal from '@/components/modals/DesativarRevendedorModal.vue';

// Initialize stores
const revendedorStore = useRevendedorStore();
const notificationStore = useNotificationStore();

// Local state
const loading = computed(() => revendedorStore.loading);
const error = computed(() => revendedorStore.error);
const revendedores = computed(() => revendedorStore.revendedores);
const filtros = computed(() => revendedorStore.filtros);
const showFilters = ref(true);

// Pagination state
const pagination = computed(() => revendedorStore.pagination);
const showNoResults = computed(() => !loading.value && revendedores.value.length === 0);

// Pagination control - simplified
const currentPage = ref(1);

// Loading state for actions
const actionLoading = ref({});

// Search timeout for debouncing
let searchTimeout = null;

// Options for status filter (ativo)
const statusOptions = ref([
  { title: 'Todos', value: '' },
  { title: 'Ativos', value: 1 },
  { title: 'Inativos', value: 0 }
]);

const showCopyNotification = ref(false);
const copyMessage = ref('');

const copyToClipboard = async (data) => {
  try {
    // Handle both direct arguments and object format
    const text = data.text || 'Texto';
    const value = data.value || data;
    
    await navigator.clipboard.writeText(value);
    
    // Configurar e exibir o snackbar
    copyMessage.value = `${text} copiado com sucesso!`;
    showCopyNotification.value = true;
    
  } catch (err) {
    console.error('Erro ao copiar para área de transferência:', err);
    // Mostrar mensagem de erro em caso de falha
    copyMessage.value = 'Erro ao copiar para a área de transferência';
    showCopyNotification.value = true;
  }
};

// Format date for API (YYYY-MM-DD)
const formatDateForApi = (dateStr) => {
  if (!dateStr) return '';
  
  // If using a date picker that already returns YYYY-MM-DD format
  if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) return dateStr;
  
  // Otherwise, convert from DD/MM/YYYY to YYYY-MM-DD
  const parts = dateStr.split('/');
  if (parts.length === 3) {
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  }
  
  return dateStr;
};

// When applying date filters, convert to API format
const aplicarFiltrosComData = () => {
  // Format dates for API before applying filters
  if (filtros.value.data_inicial) {
    filtros.value.data_inicial = formatDateForApi(filtros.value.data_inicial);
  }
  if (filtros.value.data_final) {
    filtros.value.data_final = formatDateForApi(filtros.value.data_final);
  }
  
  // Reset page to 1 when applying filters
  currentPage.value = 1;
  revendedorStore.aplicarFiltros();
};

// Handle search with debounce - simplified to remove console.log
const handleSearch = () => {
  if (searchTimeout) clearTimeout(searchTimeout);
  
  searchTimeout = setTimeout(() => {
    currentPage.value = 1;
    revendedorStore.aplicarFiltros();
  }, 500);
};

// Clear all filters
const limparFiltros = () => {
  currentPage.value = 1;
  revendedorStore.limparFiltros();
};

// For deactivation modal
const deactivateModalOpen = ref(false);
const selectedRevendedor = ref({
  id: null,
  razaosocial: '',
  ativo: 0
});

// Toggle revendedor activation status with confirmation
const toggleStatus = async (revendedor) => {
  // Skip if already processing an action for this revendedor
  if (actionLoading.value[revendedor.id]) return;
  
  const isActivating = revendedor.ativo === 0 || revendedor.ativo === '0';
  
  if (isActivating) {
    // Set loading state for this action
    actionLoading.value = { 
      ...actionLoading.value, 
      [revendedor.id]: true 
    };
    
    try {
      const response = await revendedorStore.ativarRevendedor(revendedor.id);
      
      if (response.success) {
        // Update the local state without a full refresh
        revendedor.ativo = 1;
        
        // Use notification system instead of alert
        notificationStore.showNotification(response.message || 'Revendedor ativado com sucesso', 'success');
      } else {
        // Use notification system instead of alert
        notificationStore.showNotification(response.message || 'Erro ao ativar revendedor', 'error');
      }
    } catch (error) {
      console.error('Erro ao ativar revendedor:', error);
      notificationStore.showNotification('Ocorreu um erro ao processar esta ação', 'error');
    } finally {
      // Clear loading state
      const { [revendedor.id]: _, ...remaining } = actionLoading.value;
      actionLoading.value = remaining;
    }
  } else {
    // Desativar - show modal - clone the object to avoid reactivity issues
    selectedRevendedor.value = { ...revendedor };
    deactivateModalOpen.value = true;
  }
};

// Handle revendedor deactivation (from modal)
const handleRevendedorDeactivated = (updatedRevendedor) => {
  const index = revendedores.value.findIndex(r => r.id === updatedRevendedor.id);
  if (index !== -1) {
    revendedores.value[index].ativo = updatedRevendedor.ativo;
  }
};

// Format date helper function
const formatDate = (dateString) => {
  if (!dateString) return '-';
  
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('pt-BR').format(date);
};

// Initial data fetch 
onMounted(() => {
  currentPage.value = 1;
  revendedorStore.buscarRevendedoresPage(1);
});

// Update the onPageChange method to scroll to top after page change
const onPageChange = (newPage) => {
  if (currentPage.value !== newPage) {
    currentPage.value = newPage;
    revendedorStore.buscarRevendedoresPage(newPage).then(() => {
      // Scroll to top of the page after data is loaded
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }
};
</script>

<template>
  <div>
    <v-snackbar
      v-model="showCopyNotification"
      :timeout="2000"
      color="success"
      location="top"
    >
      {{ copyMessage }}
    </v-snackbar>

    <v-row>
      <v-col cols="12" class="d-flex align-center">
        <h1 class="text-h4">Gerenciar Revendedores</h1>
        <v-spacer></v-spacer>
        <v-btn
          icon
          variant="text"
          color="primary"
          @click="showFilters = !showFilters"
          class="mr-2"
        >
          <v-icon>mdi-filter-variant</v-icon>
        </v-btn>
      </v-col>
    </v-row>

    <!-- Filters section -->
    <v-expand-transition>
      <v-card class="mb-4" v-if="showFilters">
        <v-card-text>
          <v-row>
            <!-- Data inicial filter -->
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                v-model="filtros.data_inicial"
                label="Data Inicial"
                variant="outlined"
                density="comfortable"
                hide-details="auto"
                clearable
                type="date"
                prepend-inner-icon="mdi-calendar-start"
              ></v-text-field>
            </v-col>
            
            <!-- Data final filter -->
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                v-model="filtros.data_final"
                label="Data Final"
                variant="outlined"
                density="comfortable"
                hide-details="auto"
                clearable
                type="date"
                prepend-inner-icon="mdi-calendar-end"
                :min="filtros.data_inicial"
              ></v-text-field>
            </v-col>

            <!-- Ativo filter -->
            <v-col cols="12" sm="6" md="3">
              <v-select
                v-model="filtros.ativo"
                :items="statusOptions"
                item-title="title"
                item-value="value"
                label="Status revendedor"
                variant="outlined"
                density="comfortable"
                hide-details="auto"
                prepend-inner-icon="mdi-checkbox-marked-circle-outline"
              ></v-select>
            </v-col>
          </v-row>

          <v-row>
            <!-- Razão Social filter -->
            <v-col cols="12" sm="6" md="4">
              <v-text-field
                v-model="filtros.razaosocial"
                label="Razão Social"
                variant="outlined"
                density="comfortable"
                hide-details="auto"
                clearable
                @update:model-value="handleSearch"
                @keyup.enter="revendedorStore.aplicarFiltros()"
                prepend-inner-icon="mdi-domain"
              ></v-text-field>
            </v-col>
            
            <!-- CNPJ filter -->
            <v-col cols="12" sm="6" md="4">
              <v-text-field
                v-model="filtros.cnpj"
                label="CNPJ"
                v-mask="'##.###.###/####-##'"
                variant="outlined"
                density="comfortable"
                hide-details="auto"
                clearable
                @update:model-value="handleSearch"
                @keyup.enter="revendedorStore.aplicarFiltros()"
                prepend-inner-icon="mdi-file-document-outline"
              ></v-text-field>
            </v-col>
          </v-row>
          
          <v-row class="mt-2">
            <v-col cols="12" class="d-flex">
              <v-spacer></v-spacer>
              
              <v-btn
                color="primary"
                variant="text"
                class="mr-2"
                prepend-icon="mdi-refresh"
                @click="limparFiltros"
              >
                Limpar Filtros
              </v-btn>
              
              <v-btn
                color="primary"
                prepend-icon="mdi-magnify"
                @click="aplicarFiltrosComData"
              >
                Pesquisar
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-expand-transition>

    <v-alert v-if="error" type="error" closable class="mb-4">
      {{ error }}
    </v-alert>

    <v-progress-linear v-if="loading" indeterminate color="primary"></v-progress-linear>

    <!-- Data table section styled like ListaPerfisView -->
    <v-data-table
      :headers="[
        { title: 'Nº', value: 'id', width: '60px' },
        { title: 'Razão Social', value: 'razaosocial' },
        { title: 'Responsável', value: 'responsavel' },
        { title: 'Contatos', value: 'contatos' },
        { title: 'CNPJ', value: 'cnpj' },
        { title: 'Cadastro', value: 'dtcadastro', width: '100px' },
        { title: 'Status', value: 'ativo', width: '90px' },
        { title: 'Ações', value: 'actions', sortable: false, width: '120px' }
      ]"
      :items="revendedores"
      :items-per-page="pagination.perPage"
      :loading="loading"
      class="custom-table"
    >
      <template #item="{ item }">
        <tr>
          <td>{{ item.id }}</td>
          <td>
            <div>{{ item.razaosocial }}</div>
            <div class="text-caption" v-if="item.autorizacaoanp">
              ANP: {{ item.autorizacaoanp }}
            </div>
          </td>
          <td>{{ item.responsavel || '-' }}</td>
          <td>
            <div class="mb-1" v-if="item.telefone1">
              <v-icon @click="copyToClipboard({ text: 'telefone', value: item.telefone1 })" size="small" color="primary" class="mr-1">mdi-phone</v-icon>
              {{ item.telefone1 }}
            </div>
            <div v-if="item.celular">
              <v-icon @click="copyToClipboard({ text: 'celular', value: item.celular })" size="small" color="primary" class="mr-1">mdi-cellphone</v-icon>
              {{ item.celular }}
            </div>
          </td>
          <td>{{ item.cnpj || '-' }}</td>
          <td>{{ item.dtcadastro || '-' }}</td>
          <td>
            <v-chip
              :color="item.ativo === '1' || item.ativo === 1 ? 'success' : 'error'"
            >
              {{ item.ativo === '1' || item.ativo === 1 ? 'Ativo' : 'Inativo' }}
            </v-chip>
          </td>
          <td>
            <v-tooltip text="Desativar Revendedor" v-if="item.ativo === '1' || item.ativo === 1">
              <template v-slot:activator="{ props }">
                <v-icon
                  color="error"
                  icon="mdi-close-circle-outline"
                  @click="toggleStatus(item)"
                  :loading="actionLoading[item.id]"
                  class="mr-2"
                  v-bind="props"
                ></v-icon>
              </template>
            </v-tooltip>
            
            <v-tooltip text="Ativar Revendedor" v-else>
              <template v-slot:activator="{ props }">
                <v-icon 
                  color="success"
                  icon="mdi-check-circle-outline"
                  @click="toggleStatus(item)"
                  :loading="actionLoading[item.id]"
                  class="mr-2"
                  v-bind="props"
                ></v-icon>
              </template>
            </v-tooltip>
            
            <v-tooltip text="Editar Revendedor">
              <template v-slot:activator="{ props }">
                <v-icon 
                  class="ml-2"
                  color="primary"
                  icon="mdi-pencil"
                  @click="$router.push({ name: 'revendedor-detalhes', params: { id: item.id } })"
                  v-bind="props"
                ></v-icon>
              </template>
            </v-tooltip>
          </td>
        </tr>
      </template>

      <!-- Bottom pagination - FIX: Make the v-model one-way binding -->
      <template #bottom>
        <div class="d-flex justify-space-between align-center px-4 py-2">
          <span class="text-caption text-secondary">
            Total de {{ pagination.total }} revendedor{{ pagination.total !== 1 ? 'es' : '' }}
          </span>

          <v-pagination
            v-if="pagination.lastPage > 1"
            :model-value="currentPage"
            :length="pagination.lastPage"
            :total-visible="5"
            @update:model-value="onPageChange"
            rounded
            class="my-2"
          ></v-pagination>
        </div>
      </template>
    </v-data-table>
    
    <!-- Empty state -->
    <v-card
      v-if="showNoResults"
      class="text-center py-8"
    >
      <v-icon size="64" color="grey" class="mb-4">mdi-store-off</v-icon>
      <h3 class="text-h5 mb-2">Nenhum revendedor encontrado</h3>
      <p class="text-body-1 text-grey mb-4">
        Tente ajustar seus filtros ou limpar a pesquisa.
      </p>
      <v-btn
        color="primary"
        prepend-icon="mdi-refresh"
        @click="limparFiltros"
      >
        Limpar filtros
      </v-btn>
    </v-card>
    
    <!-- Loading state -->
    <v-card
      v-if="loading && revendedores.length === 0"
      class="text-center py-8"
    >
      <v-progress-circular
        indeterminate
        color="primary"
        size="64"
        class="mb-4"
      ></v-progress-circular>
      <h3 class="text-h5 mb-2">Carregando revendedores...</h3>
    </v-card>
    
    <!-- Desativar Revendedor Modal - Use v-if to avoid rendering with null -->
    <DesativarRevendedorModal
      v-if="selectedRevendedor"
      v-model:show="deactivateModalOpen"
      :revendedor="selectedRevendedor"
      @desativado="handleRevendedorDeactivated"
    />
  </div>
</template>

<style scoped>
/* Using the same styling as in other tables */
.custom-table :deep(.v-data-table__th) {
  white-space: nowrap;
  text-transform: uppercase;
  font-size: 0.8rem;
}

.custom-table :deep(.v-data-table__tr:hover) {
  background-color: rgba(var(--v-theme-primary), 0.05) !important;
}
</style>
